import { useEffect, useState } from "react";
import { LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, Spinner, StatusPill, Empty } from "@/components/shared";

const FLOW = ["Requested", "Acknowledged", "In Progress", "Resolved"];

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.round(diff)} min ago`;
  return `${Math.round(diff / 60)} hr ago`;
}

export default function HelpRequests() {
  const [reqs, setReqs] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = () => api.get("/band/help-requests").then(({ data }) => setReqs(data));
  useEffect(() => { load(); }, []);

  const advance = async (r, status) => {
    await api.put(`/band/help-requests/${r.id}`, { status });
    toast.success(`Marked "${status}"`);
    load();
  };

  if (!reqs) return <Spinner />;
  const shown = reqs.filter((r) => (filter === "open" ? r.status !== "Resolved" : filter === "all" ? true : r.status === "Resolved"));

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Help Requests</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Triage and resolve participant support requests</p>

      <div className="flex gap-2 mt-5">
        {[{ id: "open", l: "Open" }, { id: "resolved", l: "Resolved" }, { id: "all", l: "All" }].map((f) => (
          <button key={f.id} data-testid={`help-filter-${f.id}`} onClick={() => setFilter(f.id)}
            className="cs-tap text-sm font-semibold rounded-full px-4 py-1.5" style={{ background: filter === f.id ? "var(--cs-magenta)" : "var(--cs-card)", color: "#fff", border: "1px solid var(--cs-border)" }}>
            {f.l}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Empty icon={LifeBuoy} title="Nothing here" sub="No requests match this filter." />
      ) : (
        <div className="mt-4 space-y-3">
          {shown.map((r) => {
            const idx = FLOW.indexOf(r.status);
            const next = FLOW[idx + 1];
            const urgent = r.status === "Requested";
            return (
              <div key={r.id} data-testid={`help-request-${r.id}`} className="cs-card p-4" style={{ borderColor: urgent ? "var(--cs-red)" : "var(--cs-border)" }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-head font-bold text-lg leading-tight">{r.type}</p>
                    <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>{r.user_name} · {timeAgo(r.created_at)}</p>
                    {r.note && <p className="text-sm mt-1">"{r.note}"</p>}
                  </div>
                  <StatusPill status={r.status} />
                </div>
                {next && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`help-advance-${r.id}`} onClick={() => advance(r, next)}
                      className="cs-tap flex-1 rounded-xl py-2.5 font-semibold text-sm magenta-grad text-white">
                      Mark {next}
                    </button>
                    {r.status !== "Resolved" && (
                      <button data-testid={`help-resolve-${r.id}`} onClick={() => advance(r, "Resolved")}
                        className="cs-tap rounded-xl px-4 py-2.5 font-semibold text-sm" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "var(--cs-green)" }}>
                        Resolve
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Page>
  );
}
